import { useState, useEffect } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { supabase } from '../lib/supabase';
import { useTeamActions } from './useTeamActions';

export const useLanguages = () => {
  const { selectedProject } = useWorkspace();
  const { deleteLanguageFromProject } = useTeamActions();
  const [availableLanguages, setAvailableLanguages] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadLanguages = async () => {
    if (!selectedProject) {
      setAvailableLanguages([]);
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      // Get languages for the selected project
      const { data: project, error: projectError } = await supabase
        .from('Projects')
        .select('languages')
        .eq('id', selectedProject.id)
        .single();

      if (projectError) throw projectError;

      // Set the project languages without sorting to preserve database order
      setAvailableLanguages(project?.languages || []);
    } catch (error: any) {
      setError(error.message || 'Failed to load languages');
    } finally {
      setIsLoading(false);
    }
  };

  // Load languages when project changes
  useEffect(() => {
    loadLanguages();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadLanguages reads selectedProject
  }, [selectedProject]);

  const addLanguage = async (language: string) => {
    if (!selectedProject) return;
    
    try {
      const currentLanguages = [...availableLanguages];
      if (!currentLanguages.includes(language)) {
        // Add new language to the end without sorting
        const newLanguages = [...currentLanguages, language];
        const { error: updateError } = await supabase
          .from('Projects')
          .update({ languages: newLanguages })
          .eq('id', selectedProject.id);

        if (updateError) throw updateError;
        setAvailableLanguages(newLanguages);
      }
    } catch (error: any) {
      setError(error.message || 'Failed to add language');
    }
  };

  const deleteLanguage = async (language: string) => {
    if (!selectedProject) return;
    
    try {
      // Delete all translation strings for this language and update project languages
      await deleteLanguageFromProject(selectedProject.id, language);
      
      // Update local state
      const newLanguages = availableLanguages.filter(l => l !== language);
      setAvailableLanguages(newLanguages);
    } catch (error: any) {
      setError(error.message || 'Failed to delete language');
    }
  };

  return {
    availableLanguages,
    isLoading,
    error,
    refreshLanguages: loadLanguages,
    addLanguage,
    deleteLanguage
  };
}; 